import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';

interface PaymentInstruction {
  _id: string;
  slug: string;
  title: string;
  titleArabic?: string;
  description: string;
  descriptionArabic?: string;
  steps: string[];
  stepsArabic?: string[];
  recipientName: string;
  recipientNameArabic?: string;
  recipientPhoneOrAccount: string;
  referenceNote?: string;
  referenceNoteArabic?: string;
  instructionImages: string[];
  active: boolean;
  order: number;
}

export default function PaymentInstructionsTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [list, setList] = useState<PaymentInstruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PaymentInstruction | null>(null);
  const [form, setForm] = useState<Partial<PaymentInstruction>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/payment-instructions');
      setList(res.data.data || []);
    } catch (err) {
      console.error('Failed to load payment instructions', err);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (item: PaymentInstruction) => {
    setEditing(item);
    setForm({
      title: item.title,
      titleArabic: item.titleArabic,
      description: item.description,
      descriptionArabic: item.descriptionArabic,
      steps: item.steps?.length ? item.steps : [],
      stepsArabic: item.stepsArabic?.length ? item.stepsArabic : [],
      recipientName: item.recipientName,
      recipientNameArabic: item.recipientNameArabic,
      recipientPhoneOrAccount: item.recipientPhoneOrAccount,
      referenceNote: item.referenceNote,
      referenceNoteArabic: item.referenceNoteArabic,
      instructionImages: item.instructionImages || [],
      active: item.active,
      order: item.order,
    });
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'payment-instructions');
    fd.append('type', 'image');
    const response = await api.post('/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const url = response.data?.data?.url;
    if (url) return url;
    throw new Error('Upload failed');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const urls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const url = await uploadImage(files[i]);
        urls.push(url);
      }
      setForm((f) => ({
        ...f,
        instructionImages: [...(f.instructionImages ?? []), ...urls],
      }));
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
      fileInputRef.current?.form?.reset();
    }
  };

  const removeImage = (index: number) => {
    setForm((f) => ({
      ...f,
      instructionImages: (f.instructionImages ?? []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await api.put(`/admin/payment-instructions/${editing._id}`, {
        title: form.title,
        titleArabic: form.titleArabic,
        description: form.description,
        descriptionArabic: form.descriptionArabic,
        steps: Array.isArray(form.steps) ? form.steps : [],
        stepsArabic: Array.isArray(form.stepsArabic) ? form.stepsArabic : [],
        recipientName: form.recipientName,
        recipientPhoneOrAccount: form.recipientPhoneOrAccount,
        referenceNote: form.referenceNote,
        instructionImages: Array.isArray(form.instructionImages) ? form.instructionImages : [],
        active: form.active,
        order: form.order,
      });
      await load();
      setEditing(null);
    } catch (err) {
      console.error('Failed to save', err);
      alert(t('paymentInstructions.save') + ' failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('paymentInstructions.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="bg-white rounded-lg shadow-md p-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <DocumentDuplicateIcon className="w-6 h-6 text-primary-orange" />
          {t('paymentInstructions.title')}
        </h2>
        {list.length === 0 ? (
          <p className="text-gray-500 py-4">{t('paymentInstructions.noInstructions')}</p>
        ) : (
          <div className="space-y-4">
            {list.map((item) => (
              <div
                key={item._id}
                className="border border-gray-200 rounded-lg p-4 flex flex-wrap items-center justify-between gap-4"
              >
                <div>
                  <p className="font-medium text-gray-900">{item.title}</p>
                  <p className="text-sm text-gray-500">{item.slug}</p>
                  {item.recipientName && (
                    <p className="text-sm mt-1">
                      {t('paymentInstructions.recipientName')}: {item.recipientName}
                    </p>
                  )}
                  {item.recipientPhoneOrAccount && (
                    <p className="text-sm">
                      {t('paymentInstructions.recipientPhoneOrAccount')}: {item.recipientPhoneOrAccount}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => openEdit(item)}
                  className="px-4 py-2 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm"
                >
                  {t('paymentInstructions.edit')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold mb-4">{t('paymentInstructions.edit')} — {editing.slug}</h3>
            <div className="space-y-4">
              {/* Title: EN then AR below */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentInstructions.titleEn')}</label>
                <input
                  type="text"
                  value={form.title ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">{t('paymentInstructions.titleAr')} ({t('common.optional')})</label>
                <input
                  type="text"
                  dir="rtl"
                  value={form.titleArabic ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, titleArabic: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              {/* Description: EN then AR below */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentInstructions.descriptionEn')}</label>
                <textarea
                  value={form.description ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[80px]"
                  rows={3}
                />
                <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">{t('paymentInstructions.descriptionAr')} ({t('common.optional')})</label>
                <textarea
                  dir="rtl"
                  value={form.descriptionArabic ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, descriptionArabic: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[80px]"
                  rows={3}
                />
              </div>
              {/* Steps: EN then AR below */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentInstructions.stepsEn')}</label>
                <textarea
                  value={(form.steps ?? []).join('\n')}
                  onChange={(e) => setForm((f) => ({ ...f, steps: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[80px]"
                  placeholder="One step per line"
                  rows={4}
                />
                <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">{t('paymentInstructions.stepsAr')} ({t('common.optional')})</label>
                <textarea
                  dir="rtl"
                  value={(form.stepsArabic ?? []).join('\n')}
                  onChange={(e) => setForm((f) => ({ ...f, stepsArabic: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[80px]"
                  placeholder="سطر واحد لكل خطوة"
                  rows={4}
                />
              </div>
              {/* Recipient & reference: English only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentInstructions.recipientName')}</label>
                <input
                  type="text"
                  value={form.recipientName ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, recipientName: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentInstructions.recipientPhoneOrAccount')}</label>
                <input
                  type="text"
                  value={form.recipientPhoneOrAccount ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, recipientPhoneOrAccount: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentInstructions.referenceNote')}</label>
                <input
                  type="text"
                  value={form.referenceNote ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, referenceNote: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. Order ID"
                />
              </div>
              {/* Screenshots: optional */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentInstructions.instructionImages')} ({t('common.optional')})</label>
                <p className="text-xs text-gray-500 mb-2">{t('paymentInstructions.instructionImagesHint')}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="mb-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {uploading ? t('paymentInstructions.uploading') : t('paymentInstructions.uploadScreenshots')}
                </button>
                {(form.instructionImages ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(form.instructionImages ?? []).map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img src={url} alt={`Step ${idx + 1}`} className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                        <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center rounded-b-lg">Step {idx + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
                <label className="block text-xs text-gray-500 mb-1">{t('paymentInstructions.orPasteUrls')}</label>
                <textarea
                  value={(form.instructionImages ?? []).join('\n')}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      instructionImages: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[60px] font-mono text-sm"
                  placeholder="https://..."
                  rows={2}
                />
              </div>
            </div>
            <div className={`flex gap-2 justify-end mt-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button onClick={() => setEditing(null)} className="px-4 py-2 border rounded-lg">
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary-orange text-white rounded-lg disabled:opacity-50"
              >
                {saving ? t('common.save') + '…' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
